import React from 'react';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { usePaginatedProductData } from 'src/components/AtlasV2Components/utils/tableUtils';
import { ProductEntity } from 'sl-api-connector';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { AtlasV2PromotionRowType } from 'src/components/AtlasV2Components/AtlasV2PromotionsComponents/promotionTypes';
import _startCase from 'lodash/startCase';
import { promotionTypeToDisplayName } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/constants';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { formatMetric, getAtlasSalesV2Treatment, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { getColorForMetricChangeValue } from 'src/components/BeaconV2Components/utils/chartStyles';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import moment from 'moment';
import AtlasSalesExportModal from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';
import {
  EXPORT_TYPES,
  EXPORT_TYPES_DROPDOWN_OPTIONS
} from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';

const ProductColumnDefinition = {
  headerName: 'Products',
  tableColumnProps: {
    style: {
      width: '560px',
      paddingRight: '70px'
    }
  },
  valueFormatter: (_, row: { product: ProductEntity }) => (
    <div style={{ width: '464px' }}>
      <ProductCell
        key={row?.product?.stacklineSku}
        stacklineSku={row?.product?.stacklineSku}
        title={row?.product?.title}
      />
    </div>
  )
};

const fields = [{ name: 'unitsSold', displayName: 'Units Sold', displayInTable: false }];

export interface ProductMetadata {
  stacklineSku: string;
  title: string;
  retailerSku: string;
  imageUrl?: string;
  [key: string]: any;
}

export const AtlasPromotionsV2ProductGrid = () => {
  const { isProductEntity } = useProductEntity();
  const {
    enrichedData,
    isLoading,
    isMetadataLoading,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName
  } = usePaginatedProductData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: isProductEntity ? DorisGroupByFieldNameEnum.Week : DorisGroupByFieldNameEnum.Product,
    metricType: DorisMetricTypeEnum.ProductsWithPromotionSalesData,
    fields,
    itemsPerPage: 20,
    metadataFetchSize: 100
  });

  return (
    <Flex flexDirection="column" gap={16}>
      <AtlasV2MetricListContainer
        iconList={[IconsList.export, IconsList.table]}
        entityTableHeaderProps={{
          title: 'Promotions by Product',
          customExportModal: (props: { open: boolean; close: () => void }) => (
            <AtlasSalesExportModal
              open={props.open}
              onClose={props.close}
              metricType={DorisMetricTypeEnum.ProductsWithPromotionDataExport}
              customDropdownOptions={[
                {
                  ...EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_PROMOTIONS_PRODUCT],
                  excludeSeparateByWeek: true,
                  excludeComparison: true,
                  hideCustomizeSection: true
                }
              ]}
            />
          )
        }}
        customDataSource={{
          data: enrichedData,
          isLoading: isLoading || isMetadataLoading,
          getValuesByFieldName: getPagedValuesByFieldName,
          getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
        }}
        useTableDataProps={{
          aggregationKey: 'stacklineSku'
        }}
        shouldHidePagination
        showGrid={false}
        groupBy="stacklineSku"
        sortFieldName="retailSales"
        indexName="promotions"
        headColumnDefinition={ProductColumnDefinition}
        additionalColumnDefs={[
          {
            headerName: 'Promotions',
            tableColumnProps: {
              style: {
                paddingLeft: '0px',
                paddingRight: '40px'
              }
            },
            valueFormatter: (_, row: { responseRow: AtlasV2PromotionRowType }) => {
              const promotionType = row?.responseRow?.promoType;
              const displayName = promotionTypeToDisplayName[promotionType] || promotionType;
              const dealDate = row?.responseRow?.dealDate;
              const formattedDate = moment.unix(parseInt(dealDate, 10)).format('MMM-D-YYYY');
              const formattedDisplayName = _startCase(displayName.toLowerCase().split('_'));

              return (
                <Flex flexDirection="column">
                  <Flex sx={{ justifyContent: 'flex-start' }}>
                    <Text variant="body2">{formattedDisplayName}</Text>
                  </Flex>
                  <Flex sx={{ justifyContent: 'flex-start' }}>
                    <Text variant="body2">{formattedDate}</Text>
                  </Flex>
                </Flex>
              );
            }
          },
          {
            headerName: 'Discount',
            tableColumnProps: {
              style: {
                paddingLeft: '40px',
                paddingRight: '40px'
              }
            },
            valueFormatter: (_, row: { responseRow: AtlasV2PromotionRowType }) => {
              const retailPrice = row?.responseRow?.retailPrice ?? 0;

              const percentChange = row?.responseRow?.retailPriceChangePercent;
              const salePrice = row?.responseRow?.salePrice ?? 0;

              return (
                <Flex flexDirection="column">
                  <Flex gap={8} sx={{ justifyContent: 'flex-start' }}>
                    <Flex>
                      <Text variant="body2">
                        {formatMetric(salePrice, METRICTYPE.MONEY, {
                          showFullValue: false,
                          decimalPlaces: 2
                        })}
                      </Text>
                    </Flex>
                    <Flex>
                      <Text variant="body2" color={getColorForMetricChangeValue(percentChange)}>
                        {formatMetric(percentChange, METRICTYPE.PERCENT, {
                          showFullValue: true,
                          decimalPlaces: 0
                        })}
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex sx={{ justifyContent: 'flex-start' }}>
                    <Text variant="body2">
                      from{' '}
                      {formatMetric(retailPrice, METRICTYPE.MONEY, {
                        showFullValue: false,
                        decimalPlaces: 2
                      })}
                    </Text>
                  </Flex>
                </Flex>
              );
            }
          },
          {
            headerName: 'Sales Lift',
            tableColumnProps: {
              style: {
                paddingLeft: '40px',
                paddingRight: '24px'
              }
            },
            valueFormatter: (_, row: { responseRow: AtlasV2PromotionRowType }) => {
              const promotionSales = Number(row?.responseRow?.retailSales) || 0;
              const retailSalesChange = Number(row?.responseRow?.retailSalesChange) || 0;
              const retailSalesAmount = promotionSales - retailSalesChange;
              const percentChange = row?.responseRow?.retailSalesChangePercent || 0;

              const increaseSign = percentChange > 0 ? '+' : '-';
              return (
                <Flex flexDirection="column">
                  <Flex gap={8} sx={{ justifyContent: 'flex-start' }}>
                    <Flex>
                      <Text variant="body2">
                        {formatMetric(promotionSales, METRICTYPE.MONEY, { showFullValue: false })}
                      </Text>
                    </Flex>
                    <Flex>
                      <Text variant="body2" color={getColorForMetricChangeValue(percentChange)}>
                        {increaseSign}
                        {formatMetric(percentChange, METRICTYPE.PERCENT, {
                          showFullValue: false
                        })}
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex sx={{ justifyContent: 'flex-start' }}>
                    <Text variant="body2">
                      {`from ${formatMetric(retailSalesAmount, METRICTYPE.MONEY, {
                        showFullValue: false
                      })}`}
                    </Text>
                  </Flex>
                </Flex>
              );
            }
          }
        ]}
        fields={[{ name: 'unitsSold', displayName: 'Units Sold', displayInTable: false }]}
      />
      <Flex justifyContent="flex-end">
        <Pagination
          currentPage={currentPage}
          shouldUseInfinitePagination
          onClickNext={handleNextPage}
          capPage={50}
          onClickPrevious={handlePreviousPage}
        />
      </Flex>
    </Flex>
  );
};
